import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-number-input";
import CreatableSelect from "react-select/creatable";

import TextInput from "../text-input";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const RELATIONSHIP_OPTIONS = [
  {
    label: "Assistant",
    value: "Assistant",
  },
  {
    label: "Company Representative",
    value: "Company Representative",
  },
  {
    label: "Family Member",
    value: "Family Member",
  },
  {
    label: "Service Provider",
    value: "Service Provider",
  },
];


const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      if (action.field.startsWith("phoneNumber.")) {
        const [_, field] = action.field.split(".");

        return {
          ...state,
          phoneNumber: {
            ...state.phoneNumber,
            [field]: action.value,
          },
        };
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: undefined,
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: {
          number: "",
          countryCode: "US",
        },
        relationship: null,
      };
    }
    default:
      return state;
  }
};

const SingleProspectAuthorizedSignersTable = (props) => {
  const { authorizedSigners = [], handleChange, handleToggleEdit, disabled = false } = props;
  const [primaryAuthorizedSigner, ...otherAuthorizedSigners] = authorizedSigners;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: undefined,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: {
      number: "",
      countryCode: "US",
    },
    relationship: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddAuthorizedSigner = (e) => {
    e.preventDefault();

    handleToggleEdit("authorizedSigners");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditAuthorizedSigner = (e, index) => {
    e.preventDefault();

    const authorizedSigner = authorizedSigners[index];
    if (!authorizedSigner) {
      return;
    }

    handleToggleEdit("authorizedSigners");
    dispatch({
      type: "openModal",
      payload: {
        ...authorizedSigner,
      },
    });
  };

  const handleDeleteAuthorizedSigner = (e, index) => {
    e.preventDefault();

    const authorizedSigner = authorizedSigners[index];
    if (!authorizedSigner) {
      return;
    }

    handleToggleEdit("authorizedSigners");
    dispatch({
      type: "openDeleteModal",
      payload: {
        ...authorizedSigner,
      },
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleUpdateAuthorizedSigner = async (e) => {
    e.preventDefault();

    if (!state.firstName || !state.lastName || !state.email || !state.relationship) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    const authorizedSigner = {
      _id: state._id,
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      phoneNumber: state.phoneNumber,
      relationship: state.relationship,
    };

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange(
      state._id ?
        (authorizedSigners || []).map((item) => item._id === state._id ? authorizedSigner : item) :
        [...(authorizedSigners || []), authorizedSigner],
    );
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  const handleDeleteAuthorizedSignerConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((authorizedSigners || []).filter((item) => item._id !== state._id));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <tr className="heading-row">
        <td colSpan={3}>Primary Authorized Signer</td>
        <td/>
      </tr>

      {!authorizedSigners.length && <tr>
        <td colSpan={3}>No authorized signer is saved yet</td>
        <td/>
      </tr>}

      {primaryAuthorizedSigner && <tr>
        <td colSpan={3}>
          <div className="prospect-multi-rows-data">
            {[
              [primaryAuthorizedSigner.firstName, primaryAuthorizedSigner.lastName].filter(Boolean).join(" "),
              primaryAuthorizedSigner.email,
              primaryAuthorizedSigner.phoneNumber?.number,
              primaryAuthorizedSigner.relationship?.value,
            ].filter(Boolean).map((line, i) => (
              <Fragment key={i}>{i > 0 && <br/>}{line}</Fragment>
            ))}
          </div>
        </td>
        <td>
          <button
            className="base-button icon-button evo-blue"
            onClick={(e) => {
              handleEditAuthorizedSigner(e, 0);
            }}
            disabled={disabled}
          >
            <span className="material-icons">edit</span>
          </button>

          <button
            className="base-button icon-button btn-danger ml-2"
            onClick={(e) => {
              handleDeleteAuthorizedSigner(e, 0);
            }}
            disabled={disabled}
          >
            <span className="material-icons">delete</span>
          </button>
        </td>
      </tr>}

      {otherAuthorizedSigners.length > 0 && otherAuthorizedSigners.map((authorizedSigner, index) => (
        <Fragment key={authorizedSigner._id}>
          <tr className="heading-row">
            <td colSpan={3}>Other Authorized Signer</td>
            <td/>
          </tr>

          <tr>
            <td colSpan={3}>
              <div className="prospect-multi-rows-data">
                {[
                  [authorizedSigner.firstName, authorizedSigner.lastName].filter(Boolean).join(" "),
                  authorizedSigner.email,
                  authorizedSigner.phoneNumber?.number,
                  authorizedSigner.relationship?.value,
                ].filter(Boolean).map((line, i) => (
                  <Fragment key={i}>{i > 0 && <br/>}{line}</Fragment>
                ))}
              </div>
            </td>
            <td>
              <button
                className="base-button icon-button evo-blue"
                onClick={(e) => {
                  handleEditAuthorizedSigner(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">edit</span>
              </button>

              <button
                className="base-button icon-button btn-danger ml-2"
                onClick={(e) => {
                  handleDeleteAuthorizedSigner(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">delete</span>
              </button>
            </td>
          </tr>
        </Fragment>
      ))}

      <tr>
        <td colSpan={3}>
          <button
            className="mt-20 mb-20 action-button base-button base-blue-button"
            onClick={handleAddAuthorizedSigner}
            disabled={disabled}
          >
            Add Authorized Signer
          </button>
        </td>
        <td/>
      </tr>


      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{state._id ? "Edit" : "Add"} Authorized Signer</h4>
        </div>

        <div className="modal-body base-modal prospect-authorized-signers-modal">
          <form onSubmit={handleUpdateAuthorizedSigner} className="w-100 base-form mt-20">

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="firstName"
                label="First Name"
                required
                handleChange={handleInputChange}
                value={state.firstName}
                containerClasses="flex-half"
                className="mb-0"
              />

              <TextInput
                name="lastName"
                label="Last Name"
                required
                handleChange={handleInputChange}
                value={state.lastName}
                containerClasses="flex-half"
                className="mb-0"
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="email"
                label="Email"
                handleChange={handleInputChange}
                value={state.email}
                containerClasses="flex-half"
                className="mb-0"
                type="email"
                required
              />

              <div className="flex-half">
                <div className="form-group fb-100 ml-0">
                  <label className="phoneInputLabel" htmlFor="phone">
                    Phone Number
                  </label>
                  <PhoneInput
                    placeholder="Phone Number"
                    value={state.phoneNumber?.number}
                    name="number"
                    onChange={(value) =>
                      dispatch({
                        type: "fieldChanged",
                        field: "phoneNumber.number",
                        value: value,
                      })
                    }
                    onCountryChange={(value) =>
                      dispatch({
                        type: "fieldChanged",
                        field: "phoneNumber.countryCode",
                        value: value,
                      })
                    }
                    defaultCountry={state.phoneNumber?.countryCode}
                  />
                </div>
              </div>
            </div>

            <div className="form-group fb-100 ml-0">
              <label className="base-label">
                Relationship to Primary Contact <span className="required">*</span>
              </label>
              <CreatableSelect
                className="prospect-form-select"
                classNamePrefix="select"
                value={state.relationship}
                options={RELATIONSHIP_OPTIONS}
                placeholder="Select a Relationship"
                onChange={(value) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "relationship",
                    value: value,
                  });
                }}
                name="relationship"
                menuPosition="fixed"
              />
            </div>

            <div className="w-100 mt-20 d-flex gap-20">
              <button className="base-button evo-blue flex-half" type="submit" disabled={state.saving}>
                Submit
              </button>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Authorized Signer</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This authorized signer will be permanently removed from the prospect’s profile.<br/>
            Do you want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button"
                    onClick={handleDeleteAuthorizedSignerConfirm}
                    disabled={state.saving}>
              Delete Authorized Signer
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectAuthorizedSignersTable;
